import { Injectable, computed, inject, signal } from '@angular/core';
import { doc, getDoc } from 'firebase/firestore';
import type { BillingConfigResponse, BillingPlan } from '@shared/car-types';
import { AuthService } from '../services/auth.service';
import { AnalyticsService } from './analytics.service';

import { FUNCTIONS_BASE as BASE } from '../env';

/**
 * Premium tier state. `enabled` mirrors the server: until Stripe is configured
 * it stays false and every premium UI surface stays hidden — the app is fully
 * free. Entitlements are read-only for clients (rules); the Stripe webhook is
 * the sole writer.
 */
@Injectable({ providedIn: 'root' })
export class BillingService {
  private readonly auth = inject(AuthService);
  private readonly analytics = inject(AnalyticsService);

  readonly enabled = signal(false);
  readonly plans = signal<BillingPlan[]>([]);
  readonly oneOffCheck = signal<{ priceId: string; display: string } | null>(null);
  readonly fullCheck = signal<{ priceId: string; display: string; premiumPriceId: string; premiumDisplay: string } | null>(null);
  /** 'writeoff' | 'full' — which product the ?check=success return was for. */
  readonly pendingCheckKind = signal('writeoff');
  readonly tier = signal<'free' | 'premium'>('free');
  readonly premiumUntil = signal(0);
  /** True when premium comes from a live Stripe subscription (vs an access code). */
  readonly hasStripeSub = signal(false);
  /** Registration just purchased via one-off checkout — prompts the user to run it. */
  readonly pendingCheckReg = signal('');
  readonly busy = signal(false);
  readonly error = signal('');

  readonly isPremium = computed(() => this.tier() === 'premium');
  /** Show premium locks/upsells only when billing is live AND user isn't premium. */
  readonly gated = computed(() => this.enabled() && !this.isPremium());

  constructor() {
    void this.loadConfig();
    void this.refreshEntitlement();
    // Returning from Stripe checkout?
    try {
      const params = new URLSearchParams(location.search);
      if (params.get('upgrade') === 'success') {
        this.analytics.track('checkout_success', { kind: 'subscription' });
        void this.pollEntitlement();
        history.replaceState(null, '', location.origin + location.pathname);
      }
      if (params.get('upgrade') === 'cancelled' || params.get('check') === 'cancelled') {
        // Reached Stripe and backed out — the difference between this and a
        // missing checkout_success is a payment failure rather than a change
        // of mind, which is the thing worth alerting on.
        this.analytics.track('checkout_cancelled', { oneOff: params.get('check') === 'cancelled' });
        history.replaceState(null, '', location.origin + location.pathname);
      }
      if (params.get('check') === 'success') {
        this.analytics.track('checkout_success', { kind: 'one_off_check' });
        this.pendingCheckReg.set(params.get('reg') ?? '');
        this.pendingCheckKind.set(params.get('kind') === 'full' ? 'full' : 'writeoff');
        history.replaceState(null, '', location.origin + location.pathname);
      }
    } catch {
      /* ignore */
    }
  }

  private async loadConfig(): Promise<void> {
    try {
      const res = await fetch(`${BASE}/billingConfig`, { method: 'POST' });
      const cfg = (await res.json()) as BillingConfigResponse;
      this.enabled.set(!!cfg.enabled);
      this.plans.set(cfg.plans ?? []);
      this.oneOffCheck.set(cfg.oneOffCheck ?? null);
      this.fullCheck.set((cfg as { fullCheck?: { priceId: string; display: string; premiumPriceId: string; premiumDisplay: string } | null }).fullCheck ?? null);
    } catch {
      /* stays disabled */
    }
  }

  async refreshEntitlement(): Promise<void> {
    await this.auth.ready;
    const uid = this.auth.uid();
    if (!uid) return;
    try {
      const snap = await getDoc(doc(this.auth.db, 'entitlements', uid));
      if (snap.exists()) {
        const d = snap.data() as { tier?: string; premiumUntil?: number; stripeCustomerId?: string; source?: string };
        const until = d.premiumUntil ?? 0;
        const active = d.tier === 'premium' && until + 3 * 86_400_000 > Date.now();
        this.tier.set(active ? 'premium' : 'free');
        this.premiumUntil.set(until);
        this.hasStripeSub.set(active && !!d.stripeCustomerId);
      } else {
        this.tier.set('free');
        this.hasStripeSub.set(false);
      }
    } catch {
      /* keep last known */
    }
  }

  /** Native store purchase just completed: reflect it now; the RevenueCat webhook makes it durable. */
  applyNativeEntitlement(premiumUntil: number): void {
    this.tier.set('premium');
    this.premiumUntil.set(premiumUntil);
  }

  /** After checkout: webhook lands within seconds — poll briefly. */
  async pollEntitlement(): Promise<void> {
    for (let i = 0; i < 10; i++) {
      await this.refreshEntitlement();
      if (this.isPremium()) return;
      await new Promise((r) => setTimeout(r, 2000));
    }
  }

  async startCheckout(priceId: string, registration?: string): Promise<void> {
    this.analytics.track('checkout_started', { oneOff: !!registration });
    this.busy.set(true);
    this.error.set('');
    try {
      const token = await this.auth.idToken();
      const res = await fetch(`${BASE}/createCheckoutSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ priceId, ...(registration ? { registration } : {}) }),
      });
      const j = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !j.url) throw new Error(j.error || 'Could not start checkout.');
      location.href = safeStripeUrl(j.url);
    } catch (e) {
      this.error.set((e as Error).message);
      this.busy.set(false);
    }
  }

  /** Access code → full mode ("promoCodes" server-side; entitlement written by the function). */
  async redeemCode(code: string): Promise<string> {
    this.busy.set(true);
    this.error.set('');
    try {
      const token = await this.auth.idToken();
      const res = await fetch(`${BASE}/redeemCode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ code }),
      });
      const j = (await res.json()) as { ok?: boolean; message?: string };
      if (!res.ok || !j.ok) throw new Error(j.message || 'Could not redeem that code.');
      this.analytics.track('code_redeemed');
      await this.refreshEntitlement();
      return j.message || 'Full mode unlocked.';
    } finally {
      this.busy.set(false);
    }
  }

  async openPortal(): Promise<void> {
    this.busy.set(true);
    this.error.set('');
    try {
      const token = await this.auth.idToken();
      const res = await fetch(`${BASE}/createPortalSession`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: '{}',
      });
      const j = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !j.url) throw new Error(j.error || 'Could not open the billing portal.');
      location.href = safeStripeUrl(j.url);
    } catch (e) {
      this.error.set((e as Error).message);
      this.busy.set(false);
    }
  }
}

/** The server returns a Checkout/Portal URL; navigate only if it really is Stripe over https. */
function safeStripeUrl(raw: string): string {
  const u = new URL(raw);
  if (u.protocol !== 'https:' || !/(^|\.)stripe\.com$/.test(u.hostname)) throw new Error('Unexpected checkout URL.');
  return u.toString();
}
